/**
 * Outreach sequences — persistence for governed cadence definitions
 * (`outreachSequences`).
 *
 * WHY this exists: the outreach engine's PURE half (model, step
 * scheduling, suppression) lives in src/lib/crm/outreach.ts; this
 * module is the other half — a tiny store wrapper so a sequence
 * definition survives reload and syncs like everything else. Sending
 * is a later, gated, credential'd atom — nothing here sends anything.
 *
 * Honest accounting: nothing is seeded — an empty list means no
 * sequences exist. In mock mode this lives in localStorage and flips
 * to Firestore with the same flag as the rest of the app. Mirrors
 * src/lib/db/healthHistory.ts / savedViews.ts.
 */

import { db, nowIso, uuid } from './store';
import type { OutreachSequence } from './types';
import type { SequenceStep } from '../crm/outreach';

/** Everything the caller provides; id/timestamps are derived here. */
export interface OutreachSequenceInput {
  /** Omit to create; pass an existing id to update in place. */
  id?: string;
  name: string;
  steps: SequenceStep[];
}

/** Stable name ordering for lists + subscriptions (case-insensitive). */
function byName(a: OutreachSequence, b: OutreachSequence): number {
  return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
}

/**
 * Save a sequence. Upserts by id: re-saving an existing id replaces
 * its name/steps but keeps the original createdAt, so enrollments that
 * reference the sequence stay stable. Returns the persisted record.
 */
export async function saveSequence(
  input: OutreachSequenceInput,
): Promise<OutreachSequence> {
  const id = input.id ?? uuid();
  const existing = input.id ? await db.get('outreachSequences', id) : null;
  const record: OutreachSequence = {
    id,
    name: input.name,
    steps: input.steps,
    createdAt: existing?.createdAt ?? nowIso(),
    updatedAt: nowIso(),
  };
  await db.put('outreachSequences', record);
  return record;
}

/** All sequences, sorted by name. */
export async function listSequences(): Promise<OutreachSequence[]> {
  const all = await db.list('outreachSequences');
  return [...all].sort(byName);
}

/** Delete one sequence by id. */
export async function removeSequence(id: string): Promise<void> {
  await db.remove('outreachSequences', id);
}

/** Subscribe to sequences, sorted by name. */
export function subscribeSequences(
  fn: (sequences: OutreachSequence[]) => void,
): () => void {
  return db.subscribe('outreachSequences', (rows) => fn([...rows].sort(byName)));
}
